import { doc, getDoc, runTransaction } from "firebase/firestore";
import { auth, db } from "../net/firebase";
import { validateUsername } from "./username";

/**
 * The player identity documents — see `docs/CLOUD_SAVES_DESIGN.md` §1/§2.
 *
 * `players/{uid}` holds the username a player is displayed under;
 * `displayNames/{lowercasedName}` is the uniqueness registry that makes it
 * theirs. The two only ever move together, inside one transaction, and the
 * security rules enforce that with `getAfter()` in both directions: neither
 * document can be written unless the other one agrees at commit time. That is
 * what stops a name being reserved by someone who isn't displayed under it, or
 * displayed by someone who never reserved it.
 */
export const PLAYERS = "players";
export const DISPLAY_NAMES = "displayNames";

export type ClaimResult = { ok: true } | { ok: false; reason: string };

interface PlayerDoc {
  displayName: string;
  displayNameLower: string;
  createdAtMs: number;
  provider: string;
  email: string | null;
}

/** Aborts the claim transaction from inside; never escapes this module. */
class UsernameTakenError extends Error {}

/**
 * Claims `name` for the signed-in player, or moves them onto it from whatever
 * they were called before — first claim and rename are the same transaction,
 * because the only difference is whether there is an old registry entry to free
 * and the transaction has to read the player doc to find that out either way.
 *
 * Resolves to a reason rather than throwing for the outcomes a player caused
 * (invalid or taken name); anything else is logged and reported as a generic
 * failure, since there is nothing they can do about it but try again.
 */
export async function claimUsername(name: string): Promise<ClaimResult> {
  const validated = validateUsername(name);
  if (!validated.ok) return validated;

  const user = auth.currentUser;
  if (!user || user.isAnonymous) return { ok: false, reason: "Sign in to claim a name" };

  const displayName = validated.name;
  const lower = displayName.toLowerCase();
  const uid = user.uid;
  // Read off the credential rather than the existing doc: on a rename these
  // should follow the account as it is *now* (a re-link, a changed Google email).
  const provider = user.providerData[0]?.providerId ?? "google.com";
  const email = user.email ?? null;
  const nowMs = Date.now();

  try {
    await runTransaction(db, async (tx) => {
      const registryRef = doc(db, DISPLAY_NAMES, lower);
      const playerRef = doc(db, PLAYERS, uid);
      // Both reads before any write: Firestore rejects a transaction that reads
      // after it has written.
      const registrySnap = await tx.get(registryRef);
      const playerSnap = await tx.get(playerRef);

      const holder = registrySnap.data()?.uid as string | undefined;
      if (holder !== undefined && holder !== uid) throw new UsernameTakenError();

      const existing = playerSnap.data() as PlayerDoc | undefined;
      if (existing?.displayName === displayName) return; // already theirs, exactly as spelled

      // Only ever create the registry entry. The rules forbid updating one (an
      // update is how you would take a name off its owner), so a change of
      // capitalisation alone — same lowercase key — has to leave the existing
      // entry alone. It already points at this uid, which is all `getAfter()`
      // asks of it.
      if (!registrySnap.exists()) tx.set(registryRef, { uid, claimedAtMs: nowMs });
      // Free the old name in the SAME commit, so a rename can never leave a
      // player holding two entries, or — if it half-failed — none.
      if (existing && existing.displayNameLower !== lower) {
        tx.delete(doc(db, DISPLAY_NAMES, existing.displayNameLower));
      }
      tx.set(playerRef, {
        displayName,
        displayNameLower: lower,
        createdAtMs: existing?.createdAtMs ?? nowMs,
        provider,
        email,
      } satisfies PlayerDoc);
    });
  } catch (err) {
    if (err instanceof UsernameTakenError) return { ok: false, reason: "That name is taken" };
    console.error("[fiveout] username claim failed", err);
    return { ok: false, reason: "Couldn't save that name — try again" };
  }
  return { ok: true };
}

/**
 * Renaming is the same operation as claiming (see above); the separate name
 * exists because the account screen and the first-run claim form read better
 * saying what they mean.
 */
export const changeUsername = claimUsername;

/**
 * Live "is this name free?" feedback for the claim form. Best-effort by
 * definition — a name can be taken between this read and the commit, and
 * `claimUsername`'s transaction is the only authority.
 */
export async function isUsernameAvailable(name: string): Promise<boolean> {
  const validated = validateUsername(name);
  if (!validated.ok) return false;
  try {
    const snap = await getDoc(doc(db, DISPLAY_NAMES, validated.name.toLowerCase()));
    return !snap.exists() || snap.data().uid === auth.currentUser?.uid;
  } catch (err) {
    // Offline, or the read was denied. Stay optimistic rather than telling
    // someone their name is taken on the strength of a failed lookup — the
    // transaction will still refuse it if it really is.
    console.warn("[fiveout] username availability check failed", err);
    return true;
  }
}
